/**
 * Pinia Store для аутентификации
 * Управление пользователем, токенами и состоянием авторизации
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/auth.service'
import type { User, UserRole, TotpVerifyResult } from '@/types'

/**
 * Приводит роль из ответа API к внутреннему типу.
 * Сервер может прислать как строку ("Admin"), так и число из enum UserRole (1 = Admin).
 */
function normalizeRole(role: string | number | undefined): UserRole {
  if (role === 1 || String(role).toLowerCase() === 'admin') return 'admin'
  return 'user'
}

// Ключи для localStorage
const TOKEN_KEY = 'dustar_access_token'
const REFRESH_TOKEN_KEY = 'dustar_refresh_token'
const USER_KEY = 'dustar_user'

export const useAuthStore = defineStore('auth', () => {
  // ==================== STATE ====================
  
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(localStorage.getItem(TOKEN_KEY))
  const refreshToken = ref<string | null>(localStorage.getItem(REFRESH_TOKEN_KEY))
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // ==================== GETTERS ====================
  
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const userName = computed(() => user.value?.name || 'Гость')
  const userAvatar = computed(() => user.value?.avatar || '/default-avatar.png')

  // ==================== ACTIONS ====================
  
  /**
   * Установка пользователя
   */
  function setUser(userData: User): void {
    user.value = userData
    localStorage.setItem(USER_KEY, JSON.stringify(userData))
  }

  /**
   * Загрузка пользователя из localStorage
   */
  function loadUserFromStorage(): void {
    const storedUser = localStorage.getItem(USER_KEY)
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser)
      } catch {
        user.value = null
      }
    }
  }

  /**
   * Собирает объект пользователя приложения из ответа API
   */
  function mapApiUser(apiUser: TotpVerifyResult['user']): User {
    return {
      id: apiUser.id,
      email: apiUser.email,
      name: apiUser.displayName,
      role: normalizeRole(apiUser.role),
      avatar: apiUser.avatarUrl,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
  }

  /**
   * Вход по одноразовому коду из приложения-аутентификатора.
   * Код состоит из 6 цифр и обновляется каждые 30 секунд.
   */
  async function loginWithTotp(code: string): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      const result = await authService.verifyTotp(code)

      accessToken.value = result.token
      localStorage.setItem(TOKEN_KEY, result.token)
      setUser(mapApiUser(result.user))

      return true
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 401) {
        error.value = 'Неверный код. Проверьте приложение-аутентификатор.'
      } else if (status === 429) {
        error.value = 'Слишком много попыток. Попробуйте через 15 минут.'
      } else {
        error.value = err instanceof Error ? err.message : 'Ошибка авторизации'
      }
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Обновление access токена
   */
  async function refreshAccessToken(): Promise<boolean> {
    // Refresh endpoint не реализован - просто возвращаем false
    return false
  }

  /**
   * Выход из системы
   */
  async function logout(): Promise<void> {
    // Очищаем состояние
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    
    // Очищаем localStorage
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  }

  /**
   * Получение текущего пользователя с сервера
   */
  async function fetchCurrentUser(): Promise<void> {
    if (!accessToken.value) return
    
    try {
      const apiUser = await authService.getCurrentUser()
      setUser(mapApiUser(apiUser))
    } catch {
      logout()
    }
  }

  /**
   * Обновление профиля
   */
  async function updateProfile(data: Partial<User>): Promise<boolean> {
    isLoading.value = true
    error.value = null
    
    try {
      const updatedUser = await authService.updateProfile(data)
      setUser(updatedUser)
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Ошибка обновления профиля'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Загрузка аватара
   */
  async function uploadAvatar(file: File): Promise<boolean> {
    isLoading.value = true
    error.value = null
    
    try {
      const result = await authService.uploadAvatar(file)
      
      if (user.value) {
        user.value.avatar = result.avatarUrl
        localStorage.setItem(USER_KEY, JSON.stringify(user.value))
      }
      
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Ошибка загрузки аватара'
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Очистка ошибок
   */
  function clearError(): void {
    error.value = null
  }

  // Инициализация при создании store
  loadUserFromStorage()

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,
    
    // Getters
    isAuthenticated,
    isAdmin,
    userName,
    userAvatar,
    
    // Actions
    loginWithTotp,
    logout,
    refreshAccessToken,
    fetchCurrentUser,
    updateProfile,
    uploadAvatar,
    clearError,
  }
})
